import {
  isTerrainStyle,
  WORLDS,
  buildTerrain,
  initialAmmo,
  MAX_HP,
  MAX_WIND,
  resolveTurn,
  settleY,
  tankCenter,
  WATER_Y,
  WORLD_W,
  type GameState,
  type Tank,
  type TankLook,
  type Terrain,
  type TerrainStyle,
  type TurnEvents,
  type TurnInput,
} from '@tanky/shared';

const STORAGE_KEY = 'tanky.training';
export const MAX_TARGETS = 12;

interface Settings {
  wind: number;
  style: TerrainStyle;
  seed: number;
  playerX: number;
  targets: number[];
}

export interface TrainingStats {
  shots: number;
  hits: number;
  damage: number;
  best: number;
}

function unlimitedAmmo(): Tank['ammo'] {
  const a = initialAmmo();
  for (const k of Object.keys(a) as (keyof typeof a)[]) a[k] = -1;
  return a;
}

/**
 * Tréninková relace – běží celá v prohlížeči nad sdílenou simulací.
 * Hráč je jediný skutečný tank, ostatní jsou cvičné cíle.
 */
export class TrainingSession {
  settings: Settings;
  state!: GameState;
  /** Terén pro simulaci (zobrazovaný terén dostává opy přes přehrávání). */
  private sim!: Terrain;
  stats: TrainingStats = { shots: 0, hits: 0, damage: 0, best: 0 };
  private nextTargetId = 1;

  constructor(
    private readonly me: { id: string; name: string; look: TankLook | null },
  ) {
    this.settings = this.load();
    this.rebuild();
  }

  private load(): Settings {
    const def: Settings = { wind: 0, style: 'hills', seed: Math.floor(Math.random() * 2 ** 31), playerX: 260, targets: [900, 1200] };
    try {
      const s = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
      if (s && typeof s === 'object') {
        return {
          wind: Math.max(-MAX_WIND, Math.min(MAX_WIND, Number(s.wind) || 0)),
          style: isTerrainStyle(s.style) ? s.style : 'hills',
          seed: Number.isInteger(s.seed) ? s.seed : def.seed,
          playerX: Math.max(20, Math.min(WORLD_W - 20, Number(s.playerX) || def.playerX)),
          targets: Array.isArray(s.targets) ? s.targets.filter((x: unknown) => typeof x === 'number').slice(0, MAX_TARGETS) : def.targets,
        };
      }
    } catch {
      /* poškozené nebo nedostupné úložiště */
    }
    return def;
  }

  private save() {
    const player = this.state?.tanks?.find((t) => !t.target);
    if (player) this.settings.playerX = Math.round(player.x);
    if (this.state?.tanks) {
      this.settings.targets = this.state.tanks.filter((t) => t.target).map((t) => Math.round(t.x));
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      /* ignorovat */
    }
  }

  /** Postaví na zadaném x tank stojící na povrchu (u jeskyní nejvyšší povrch). */
  private standAt(x: number, fromY = 0): number {
    let y = Math.max(0, Math.round(fromY));
    while (y > 0 && this.sim.solid(x, y)) y--;
    return Math.min(settleY(this.sim, x, y), WATER_Y - 1);
  }

  private makeTarget(x: number, y: number): Tank {
    const n = this.nextTargetId++;
    return {
      id: `target-${n}`,
      name: `🎯 Terč ${n}`,
      color: '#ff5c6c',
      x,
      y,
      hp: MAX_HP,
      alive: true,
      angle: 90,
      power: 0,
      ammo: unlimitedAmmo(),
      target: true,
    };
  }

  /** Nová mapa ze seedu (bez kráterů) se stejnými pozicemi hráče a cílů. */
  rebuild() {
    const { seed, style, wind, playerX, targets } = this.settings;
    this.sim = buildTerrain(seed, style, []);
    this.nextTargetId = 1;
    const player: Tank = {
      id: this.me.id,
      name: this.me.name,
      color: this.me.look?.color ?? '#3498db',
      ...(this.me.look ? { look: this.me.look } : {}),
      x: playerX,
      y: this.standAt(playerX),
      hp: MAX_HP,
      alive: true,
      angle: playerX < WORLD_W / 2 ? 60 : 120,
      power: 60,
      ammo: unlimitedAmmo(),
    };
    const tanks = [player, ...targets.map((x) => this.makeTarget(x, this.standAt(x)))];
    // Na Měsíci nefouká, ať je v nastavení cokoli
    this.state = { seed, style, ops: [], tanks, current: 0, turn: 1, wind: WORLDS[style].wind ? wind : 0, status: 'playing', winner: null };
    this.save();
  }

  newMap(style: TerrainStyle) {
    this.settings.style = style;
    this.settings.seed = Math.floor(Math.random() * 2 ** 31);
    this.rebuild();
  }

  resetTerrain() {
    this.save();
    this.rebuild();
  }

  setWind(w: number) {
    this.settings.wind = Math.max(-MAX_WIND, Math.min(MAX_WIND, Math.round(w)));
    this.state = { ...this.state, wind: WORLDS[this.state.style].wind ? this.settings.wind : 0 };
    this.save();
  }

  /** Klepnutí v režimu umisťování: na cíli ho odebere, jinde přidá nový. */
  toggleTargetAt(wx: number, wy: number): 'added' | 'removed' | 'full' | 'none' {
    const hit = this.state.tanks.find((t) => {
      if (!t.target || !t.alive) return false;
      const c = tankCenter(t);
      return Math.hypot(c.x - wx, c.y - 6 - wy) < 22;
    });
    const tanks = this.state.tanks.slice();
    if (hit) {
      this.state = { ...this.state, tanks: tanks.filter((t) => t !== hit) };
      this.save();
      return 'removed';
    }
    if (tanks.filter((t) => t.target && t.alive).length >= MAX_TARGETS) return 'full';
    const x = Math.round(Math.max(12, Math.min(WORLD_W - 12, wx)));
    const y = this.standAt(x, wy);
    if (y >= WATER_Y - 1) return 'none';
    tanks.push(this.makeTarget(x, y));
    this.state = { ...this.state, tanks };
    this.save();
    return 'added';
  }

  reviveTargets() {
    const tanks = this.state.tanks.map((t) => (t.target ? { ...t, hp: MAX_HP, alive: true, y: this.standAt(t.x, t.y - 20) } : t));
    this.state = { ...this.state, tanks };
  }

  clearTargets() {
    this.state = { ...this.state, tanks: this.state.tanks.filter((t) => !t.target) };
    this.save();
  }

  /** Lokální výstřel. Vrací události pro přehrání a stav po tahu. */
  fire(input: TurnInput): { events: TurnEvents; state: GameState } {
    const { state, events } = resolveTurn(this.state, this.sim, this.me.id, input);
    const dealt = events.damage.filter((d) => d.id !== this.me.id).reduce((a, d) => a + d.amount, 0);
    this.stats.shots++;
    if (dealt > 0) this.stats.hits++;
    this.stats.damage += dealt;
    this.stats.best = Math.max(this.stats.best, dealt);

    // Trénink nikdy nekončí: hráč je zase na tahu, s plnou municí a zdravím
    const player = state.tanks.find((t) => t.id === this.me.id)!;
    if (!player.alive || player.y >= WATER_Y) {
      player.x = this.settings.playerX;
      player.y = this.standAt(player.x);
    }
    player.alive = true;
    player.hp = MAX_HP;
    player.ammo = unlimitedAmmo();
    const next: GameState = {
      ...state,
      current: state.tanks.indexOf(player),
      status: 'playing',
      winner: null,
      wind: this.state.wind,
    };
    this.settings.playerX = player.x;
    this.state = next;
    this.save();
    return { events, state: next };
  }
}
